import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "../../../../../api/person/queries";
import { personSanktionForm } from "../../../../../config/form/person/personSanktionerFormConfig";
import { defaultPersonSanktionColumns } from "../../../../../config/person";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import IngenRegistreringer from "../IngenRegistreringer";
import OpretNyButtonBlock from "../OpretNyButtonBlock";
import { PersonDto, PersonSanktion } from "../../../../../interfaces/schema";
import { PersonSanktionField } from "../../../../../interfaces/personer";
import { SortBy, IColumn } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import { orderBy } from "../../../../../util/orderBy";

export default function Sanktioner({
  columns,
  personSanktion,
  setActiveColumns,
  invalidateData,
  personId,
}: {
  personId: string;
  personSanktion: PersonSanktion[];
  columns: IColumn<PersonSanktionField>[];
  setActiveColumns: React.Dispatch<React.SetStateAction<PersonSanktionField[]>>;
  invalidateData: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const isAuth = hasPermission(user, "DisciplinaertWrite");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personSanktionerOmraaderSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  const { data } = useQuery<{
    person: PersonDto;
  }>(PERSON_BY_ID_QUERY(), {
    variables: { id: personId },
  });
  // event handlers
  function downloadHandler() {
    tableToXLSX(columns, personSanktion, {
      fileName: `sanktion-for-${data?.person.kundeNr}`,
      additionalData: {
        kundenr: data?.person.kundeNr,
      },
    });
  }

  if (!personSanktion.length) {
    return (
      <IngenRegistreringer
        editConfig={isAuth ? personSanktionForm({ personId }) : undefined}
        invalidateData={invalidateData}
      />
    );
  }
  return (
    <>
      <ButtonDownload onClick={downloadHandler} />

      <ModalProvider>
        <TableRoot>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            {getHeaderActiveColumns(columns, {
              defaultSortBy,
              setSortBy,
              sortBy,
            })}
          </TableHead>
          <TableBody>
            {orderBy(
              personSanktion,
              sortBy.map((s) => s.field),
              sortBy.map((s) => s.direction),
            ).map((s) => {
              return (
                <TableRow
                  key={s.id}
                  editConfig={
                    isAuth
                      ? personSanktionForm({
                          personId,
                          personSanktionId: s.id,
                        })
                      : undefined
                  }
                  invalidateData={invalidateData}
                >
                  {columns
                    .filter((col) => col.active)
                    .map((col) => {
                      return <TD key={col.field} col={col} rowData={s} />;
                    })}
                </TableRow>
              );
            })}
          </TableBody>
        </TableRoot>
        <ColumnEditorModal
          defaultColumns={defaultPersonSanktionColumns}
          columns={columns}
          setActiveColumns={setActiveColumns}
        />
        <OpretNyButtonBlock
          editConfig={isAuth ? personSanktionForm({ personId }) : undefined}
          invalidateData={invalidateData}
        />
      </ModalProvider>
    </>
  );
}

const defaultSortBy: SortBy<PersonSanktionField>[] = [
  {
    direction: "desc",
    field: "retskendelseDato",
  },
  {
    direction: "desc",
    field: "naevnskendelseDato",
  },
];
